<template>
	<div class="jwtv-lp__container">
		<div v-if="isLoading" class="spinner">
			<SpinnerDots />
		</div>

		<Top10JWTVModule v-if="top10Module" :moduleData="top10Module" class="top-10-section" />

		<div class="modules container-fluid container-max-width">
			<ModulesLoader
				:modules="filteredModules"
				:modulesOffset="1"
				:modulesConfig="{
					allowedMonetizationTags: ['free', 'tvod'],
				}"
			>
				<template #footer>
					<JWTVEndCard v-if="!isLoading" />
				</template>
			</ModulesLoader>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { HomeModules } from '@/features/onboarded-home/types';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';

import SpinnerDots from '@/features/ui/components/SpinnerDots.vue';
import ModulesLoader from '@/features/onboarded-home/components/ModulesLoader.vue';
import JWTVEndCard from '@/features/onboarded-home/components/JWTVEndCard.vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

import { computed } from 'vue';
import Top10JWTVModule from '@/features/jwtv-titles-rental/components/modules/Top10JWTVModule.vue';
import {
	GetTvHomeModulesDocument,
	GetTvHomeModulesQuery,
	GetTvHomeModulesQueryVariables,
} from '@/features/jwtv-home/graphql/queries/GetTVHomeModules.query';
import { useModuleContextData } from '@/features/onboarded-home/tracking';
import { storeToRefs } from 'pinia';

const { language, country } = storeToRefs(useLanguageStore());
const { activeDomain, activeRoute } = storeToRefs(useRoutingStore());
const { provideModulePlacement } = useModuleContextData();

provideModulePlacement(() => 'JWTV_LP');

// const enabled = computed(() => route.name === 'app.home');
const { result, loading } = useQuery<GetTvHomeModulesQuery, GetTvHomeModulesQueryVariables>(
	GetTvHomeModulesDocument,
	() => ({
		fullPath: activeRoute.value.path,
		site: activeDomain.value,
		country: country.value,
		language: language.value,
		locale: `${language.value.toLowerCase()}-${country.value.toUpperCase()}`,
		watchNowFilter: {
			packages: ['jwt'],
			withFallback: true,
		},
		packages: ['jwt'],
		includeStandaloneModules: true,
	}),
	{
		fetchPolicy: 'cache-first',
		errorPolicy: 'all',
		enabled: computed(() => activeRoute.value?.name === 'app.jwtv.landing-page'),
	}
);

const modules = computed(() => {
	const node = result.value?.urlV2.node;
	const _modules = (node as AssertByTypename<typeof node, 'HomePage'>)?.modules ?? [];

	return _modules.filter(module => module != null) as HomeModules;
});

const top10Module = computed(() => {
	return modules.value.find(module => module?.__typename === 'HMTop10InYourCountry');
});

const filteredModules = computed(() => {
	return modules.value.filter(module => module?.__typename !== 'HMTop10InYourCountry');
});

const isLoading = computed(() => loading.value || modules.value.length === 0);
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';

.jwtv-lp__container {
	display: flex;
	flex-direction: column;
	gap: 40px;

	width: 100%;
	padding-bottom: 16px;

	.top-10-section {
		display: flex;
		flex-direction: column;
		width: 100%;
	}

	.modules {
		width: 100%;
		@media (max-width: 1050px) {
			padding-left: 15px !important;
			padding-right: 15px !important;
		}
	}

	:deep(.top-titles-hero__header),
	:deep(.hero-titles) {
		width: 100%;
		@media (max-width: 1050px) {
			padding-left: 15px !important;
			padding-right: 15px !important;
		}
	}

	h2,
	h3 {
		color: $jw-color-white;
		font-weight: 900;
		font-size: 40px;
		line-height: 45px;
	}

	h3 {
		font-size: 20px;
		line-height: 28px;
	}

	h6 {
		color: var(--jw-color-primary);
		font-size: 13px;
		letter-spacing: 2.5px;
		line-height: 20px;
		text-shadow: 0 9px 9px rgba($jw-color-gold, 0.45);
		text-transform: uppercase;
	}

	&__hero {
		color: $jw-color-white;
		position: relative;

		&__header {
			display: flex;
			padding-top: 8px;
			align-items: center;
			justify-content: space-between;
			margin-block-end: 32px;

			&__title {
				display: flex;
				align-items: center;

				.calendar-icon {
					margin-inline-start: 16px;
				}

				h1 {
					font-size: 32px;
					line-height: 40px;
					font-weight: 900;
					color: $jw-color-white;
					margin: 0;

					@include md {
						font-size: 40px;
						line-height: 48px;
						padding: 8px 0;
					}

					@media (max-width: $screen-xs-min) {
						font-size: 26px;
						line-height: 34px;
					}
				}
			}

			&__left,
			&__left--full {
				margin-inline-end: 2em;
				width: 100%;
			}

			&__right {
				max-width: 50%;
			}
		}
	}

	& .hidden-horizontal-scrollbar__items:first-child {
		padding-inline-start: 16px;

		@include lg {
			padding-inline-start: 48px;
		}
	}

	& .hidden-horizontal-scrollbar:hover .hidden-horizontal-scrollbar__nav {
		width: 48px;

		@include sm {
			display: none !important;
		}
	}

	.spinner {
		display: flex;
		justify-content: center;
		padding-block: 48px;
		height: 100vh;
	}
}
</style>
